import { useEffect, useState } from 'react';

import {
    DRIVER_ADMINISTRATION_API_LIMIT,
    type GetDriversResponse,
    useGetDriversAfterQuery,
    useGetDriversQuery,
} from '#api/driver-administration/driverAdministration.slice.ts';
import type { Driver } from '#domain/model/Driver.ts';

const mergeDriversById = (currentDrivers: Driver[], incomingDrivers: Driver[]) => {
    if (currentDrivers.length === 0) {
        return incomingDrivers;
    }

    const mergedDrivers = new Map(currentDrivers.map(driver => [driver.id, driver]));
    incomingDrivers.forEach(driver => {
        mergedDrivers.set(driver.id, driver);
    });

    return Array.from(mergedDrivers.values());
};

const updatePaginationState = (response: GetDriversResponse, apiLimit: number) => {
    const shouldFetchMore = response.drivers.length === apiLimit && response.after;

    if (!shouldFetchMore) {
        return {
            after: undefined,
            skipAfterQuery: true,
        };
    }

    return {
        after: response.after,
        skipAfterQuery: false,
    };
};

export const useFetchDrivers = (apiLimit = DRIVER_ADMINISTRATION_API_LIMIT) => {
    const [drivers, setDrivers] = useState<Driver[]>([]);
    const [after, setAfter] = useState<string | undefined>(undefined);
    const [skipAfterQuery, setSkipAfterQuery] = useState(true);

    const {
        isLoading: isLoadingInitialDrivers,
        data: firstDriversPage,
        error: driversError,
        refetch: refetchInitialDrivers,
        isFetching: isFetchingInitialDrivers,
    } = useGetDriversQuery(
        { limit: apiLimit },
        {
            pollingInterval: 5 * 60 * 1000,
            skipPollingIfUnfocused: true,
        }
    );

    const {
        data: driversAfterPage,
        isSuccess: isSuccessAfter,
        isError: isErrorAfter,
    } = useGetDriversAfterQuery({ limit: apiLimit, after: after ?? '' }, { skip: skipAfterQuery || !after });

    const handleFirstPageResponse = (response: GetDriversResponse) => {
        setDrivers(response.drivers);

        const nextPaginationState = updatePaginationState(response, apiLimit);
        setAfter(nextPaginationState.after);
        setSkipAfterQuery(nextPaginationState.skipAfterQuery);
    };

    const handleAfterPageResponse = (response: GetDriversResponse) => {
        setDrivers(currentDrivers => mergeDriversById(currentDrivers, response.drivers));

        const nextPaginationState = updatePaginationState(response, apiLimit);
        setAfter(nextPaginationState.after);
        setSkipAfterQuery(nextPaginationState.skipAfterQuery);
    };

    const refetchDrivers = async () => {
        try {
            const { data } = await refetchInitialDrivers();
            if (data) {
                handleFirstPageResponse(data);
            }
        } catch (error) {
            console.error('Error fetching drivers from driver administration:', error);
        }
    };

    useEffect(() => {
        if (!firstDriversPage) {
            setDrivers([]);
            setAfter(undefined);
            setSkipAfterQuery(true);
            return;
        }

        handleFirstPageResponse(firstDriversPage);
    }, [firstDriversPage]);

    useEffect(() => {
        if (!isSuccessAfter || !driversAfterPage) {
            return;
        }

        handleAfterPageResponse(driversAfterPage);
    }, [driversAfterPage, isSuccessAfter]);

    useEffect(() => {
        if (isErrorAfter) {
            setAfter(undefined);
            setSkipAfterQuery(true);
        }
    }, [isErrorAfter]);

    return {
        drivers,
        isLoadingInitialDrivers,
        isFetchingDrivers: isFetchingInitialDrivers,
        driversError,
        refetchDrivers,
    };
};
